import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function ChangePasswordPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const forced = Boolean(user && user.must_change_password);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) {
      setError("Le nuove password non coincidono");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      setUser(data);
      toast.success("Password aggiornata");
      navigate(data.role === "admin" ? "/admin" : data.role === "super_admin" ? "/superadmin" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <Card className="w-full max-w-md institutional-card" data-testid="change-password-page">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <KeyRound className="h-5 w-5 text-[#003366]" />
            <h1 className="font-display text-xl font-bold text-slate-900">Cambia password</h1>
          </div>
          {forced && (
            <p data-testid="change-password-forced-note"
              className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Per motivi di sicurezza, al primo accesso devi impostare una nuova password personale prima di
              poter accedere al pannello.
            </p>
          )}
          <form onSubmit={submit} className="space-y-4 mt-5">
            <div>
              <Label htmlFor="cp-current">Password attuale</Label>
              <Input data-testid="change-password-current" id="cp-current" type="password" required value={form.current}
                onChange={(e) => setForm({ ...form, current: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="cp-next">Nuova password (min. 8 caratteri)</Label>
              <Input data-testid="change-password-new" id="cp-next" type="password" required minLength={8} value={form.next}
                onChange={(e) => setForm({ ...form, next: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="cp-confirm">Conferma nuova password</Label>
              <Input data-testid="change-password-confirm" id="cp-confirm" type="password" required minLength={8} value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
            </div>
            {error && <p data-testid="change-password-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="change-password-submit" type="submit" disabled={busy} className="w-full bg-[#003366] hover:bg-[#002244]">
              {busy ? "Aggiornamento..." : "Aggiorna password"}
            </Button>
          </form>
          {forced && (
            <button type="button" data-testid="change-password-logout"
              className="mt-4 text-xs text-slate-500 hover:text-slate-800 w-full text-center"
              onClick={async () => { await logout(); navigate("/auth"); }}>
              Esci e torna al login
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
